// eslint-disable-next-line no-restricted-imports
import { Image } from "expo-image";
import { router, usePathname } from "expo-router";
import { Platform, View } from "react-native";
import { withUnistyles } from "react-native-unistyles";

import Pressable from "$src/core/components/atoms/Pressable";
import Text from "$src/core/components/atoms/Text/Text";
import useAnalytics from "$src/core/hooks/use-analytics";
import useResponsiveImage from "$src/core/hooks/use-responsive-image";
import useTranslation from "$src/core/hooks/use-translation";

import $, { IMAGE_DIMENSIONS } from "./NavBar.web.styles";

type ItemType = {
  name: string;
  label?: string;
  icon?: string;
  isHidden?: boolean;
};

type Props = {
  items: ItemType[];
};

const UniImage = withUnistyles(Image);

const Item = ({ item }: { item: ItemType }) => {
  const pathname = usePathname();
  const { trackEvent } = useAnalytics();

  const iconImage = useResponsiveImage(item.icon, {
    dimensions: IMAGE_DIMENSIONS.icon,
  });

  const handlePress = () => {
    router.navigate(`/${item.name}`);
    trackEvent("button_interaction", {
      button_name: `nav_bar_item_${item.name}`,
      button_type: "nav_bar_item",
      button_label: item.label,
    });
  };

  const isActive = pathname.startsWith(`/${item.name}`);

  return (
    <Pressable
      accessibilityRole="link"
      style={$.tab({ isActive })}
      onPress={handlePress}
    >
      <View style={$.tabInner}>
        <UniImage
          uniProps={(theme) => ({
            tintColor: isActive
              ? theme.colors.foregroundOnInverted
              : theme.colors.foregroundDefault,
          })}
          source={iconImage}
          style={$.tabIcon}
          contentFit="contain"
          accessibilityRole="none"
          accessibilityLabel={item.label}
        />
        <Text
          preset="heading7"
          style={$.tabLabel({ isActive })}
          accessibilityRole="text"
        >
          {item.label}
        </Text>
      </View>
    </Pressable>
  );
};

const NavBar = ({ items }: Props) => {
  const { t } = useTranslation();

  return (
    <View
      style={$.container}
      {...(Platform.OS === "web"
        ? {
            role: "navigation",
            "aria-label": t("COMMON/NAVIGATION/ACCESSIBILITY_LABEL"),
          }
        : {})}
    >
      {items
        .filter((item) => !item.isHidden)
        .map((item, index) => (
          <Item key={index} item={item} />
        ))}
    </View>
  );
};

export default NavBar;
